import {
  BUYING_POWER,
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  BlotterTableOverlay,
  Button,
  ButtonVariants,
  ExportMenuItems,
  FormControlSizes,
  IconButton,
  IconName,
  MixpanelEvent,
  columnTypes,
  filterByCellValueMenuItem,
  filterByColumnMainMenuItems,
  getShowJSONContextItem,
  removeEmptyFilters,
  useAccordionFilterBuilder,
  useBlotterGroupingToggling,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useConstant,
  useGetDefaultContextMenuItems,
  useJsonModal,
  useMixpanel,
  usePersistedBlotterTable,
  useWsBlotterTable,
  type BlotterGroupingDefinition,
  type BuyingPower,
  type Column,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useMemo } from 'react';

import type { GetContextMenuItemsParams, GetMainMenuItemsParams, StateUpdatedEvent } from 'ag-grid-community';
import {
  colIDToFilterBuilderKey,
  toServerFilter,
  useBuyingPowerBlotterFilter,
  type BuyingPowerBlotterFilter,
} from './useBuyingPowerBlotterFilter';
import { useBuyingPowerColumns } from './useBuyingPowerColumns';

type BuyingPowerGroupings = 'Market Account' | 'Currency';
type GroupableBuyingPowerColumns = keyof BuyingPower;
const GROUPINGS: BlotterGroupingDefinition<BuyingPowerGroupings, GroupableBuyingPowerColumns>[] = [
  {
    key: 'Market Account',
    label: 'By Market Account',
    rowGroupColumns: ['MarketAccount'],
    buttonDataTestId: 'by-market-account-button',
  },
  {
    key: 'Currency',
    label: 'By Currency',
    rowGroupColumns: ['Currency'],
    buttonDataTestId: 'by-currency-button',
  },
];

export interface BuyingPowerBlotterTableProps {
  blotterID: string;
  tabLabel?: string;
  defaultColumns?: Column[];
  defaultFilter?: BuyingPowerBlotterFilter;
  onCloneTab?: () => void;
}

export function BuyingPowerBlotterTable({
  blotterID,
  tabLabel,
  defaultColumns,
  defaultFilter,
  onCloneTab,
}: BuyingPowerBlotterTableProps) {
  const mixpanel = useMixpanel();
  const columns = useBuyingPowerColumns({ defaultColumns });

  const persistedBlotterTable = usePersistedBlotterTable(blotterID, {
    filter: defaultFilter,
  });

  const filterResults = useBuyingPowerBlotterFilter({
    persistedBlotterTable,
  });

  const { filterBuilderProps } = filterResults;

  // if a symbol filter exists, we don't open the accordion by default
  const initialOpen = useConstant(
    !filterBuilderProps.initialFilterClauses.find(clause => clause.key === 'Symbols' && clause.selections?.length > 0)
  );

  const filterBuilderAccordion = useAccordionFilterBuilder({
    // Since filter is required, we open the accordion by default unless filter already exists
    accordionProps: { initialOpen },
    filterBuilderProps,
  });

  const getExtraMainMenuItems = useCallbackRef((params: GetMainMenuItemsParams) => {
    return filterByColumnMainMenuItems({
      params,
      colIDToFilterBuilderKey,
      openClause: filterBuilderAccordion.openClause,
      mixpanel,
    });
  });

  const { handleClickJson, jsonModal } = useJsonModal();
  const defaultContextMenuItems = useGetDefaultContextMenuItems();

  const getContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams) => {
    const data = params.node?.data;
    const items = compact([
      ...filterByCellValueMenuItem({
        params,
        filterableProperties: filterBuilderProps.properties,
        openClause: filterBuilderAccordion.openClause,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
      ...(data ? [getShowJSONContextItem({ params, handleClickJson }), 'separator'] : []),
      ...defaultContextMenuItems(params),
    ]);
    return items;
  });

  const handleGridStateUpdated = useCallbackRef((event: StateUpdatedEvent) => {
    onGroupingStateUpdated(event);
  });

  const autoGroupColumnDef = useMemo(
    () =>
      columnTypes.group({
        type: 'group',
        editable: false,
        initialHide: false,
        sortable: true,
        suppressColumnsToolPanel: false,
        params: {
          suppressCount: true,
        },
      }),
    []
  );

  const hasRequiredFilterSelections = useMemo(() => {
    // we need a symbol to be selected
    const clause = filterBuilderAccordion.filterBuilder.filterClausesByPropertyKey.get(
      'Symbols' satisfies keyof BuyingPowerBlotterFilter
    );
    return clause != null && clause.selections.length > 0;
  }, [filterBuilderAccordion.filterBuilder.filterClausesByPropertyKey]);

  const request = useMemo(
    () => ({
      tag: blotterID,
      name: BUYING_POWER,
      ...removeEmptyFilters(toServerFilter(filterResults.filter)),
    }),
    [blotterID, filterResults.filter]
  );

  const blotterTable = useWsBlotterTable({
    request,
    allowRequest: hasRequiredFilterSelections,
    rowID: 'rowID' satisfies keyof BuyingPower,
    persistence: persistedBlotterTable,
    columns,
    getExtraMainMenuItems,
    gridOptions: {
      onStateUpdated: handleGridStateUpdated,
      showOpenedGroup: true,
      autoGroupColumnDef,
      groupRemoveLowestSingleChildren: true,
      getContextMenuItems,
    },
    exportOptions: {
      name: 'Buying Power',
      tabLabel: tabLabel,
    },
  });

  const { setRowGroupColumns, expandAllGroups, collapseAllGroups } = blotterTable;

  const { onStateUpdated: onGroupingStateUpdated, groupingToggleButtons } = useBlotterGroupingToggling({
    blotterName: 'buying power',
    setRowGroupColumns,
    groupings: GROUPINGS,
    allowNoGrouping: true,
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab?.();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        accordionBodyPrefix={groupingToggleButtons}
        prefix={
          <>
            <IconButton
              icon={IconName.ListExpand}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={expandAllGroups}
            />
            <IconButton
              icon={IconName.ListCollapse}
              size={FormControlSizes.Small}
              variant={ButtonVariants.Default}
              onClick={collapseAllGroups}
            />
          </>
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            {onCloneTab && (
              <Button
                startIcon={IconName.Duplicate}
                variant={ButtonVariants.Default}
                size={FormControlSizes.Small}
                onClick={handleCloneTab}
              >
                Clone Tab
              </Button>
            )}
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} hidden={!hasRequiredFilterSelections} />
      {!hasRequiredFilterSelections && (
        <BlotterTableOverlay>
          <span data-testid="select-symbol-message">Please select a Symbol</span>
        </BlotterTableOverlay>
      )}
      {jsonModal}
    </>
  );
}
